import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type { GhlProbeCount, GhlReadStatus } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { ApiRequestError } from "../api/http";
import { useTab } from "../api/queries";
import { AboutBox } from "../design/components/AboutBox";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { SettingsLayout } from "../design/components/SettingsLayout";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { useAccess, useMenuSubject, type Access } from "../session/access";
import { switchTab } from "../session/switch-tab";
import { whenParts } from "./close-import-format";
import { GhlImportPart } from "./GhlImportPart";
import { GhlWritePart } from "./GhlWritePart";
import { ghlReadQuery, useGhlReadActions } from "./ghl-read-queries";
import "./close-import.css";

// Impostazioni → «Importazione ed esportazione» → «GoHighLevel» (INGR-29, scelta di Claude 24/10/2026 su richiesta di Lucio), la
// pagina sorella di «Importazione guidata da Close»: la vedono chi ha `import_leads` e il Super admin di Gioia365. Il Super admin di
// Gioia365 e il Super admin dell'azienda (`canManage`, INGR-29, confermata da Lucio il 4/11/2026) salvano o tolgono la chiave (il Private
// Integration Token in un campo password, che non torna mai dal server, e il Location ID) e provano la lettura, che dice soltanto numeri
// ed esiti. Gli altri leggono soltanto se la chiave c'è e l'ultima prova; al Super admin di Gioia365 entrato come persona del team la
// scorciatoia «Entra come Super admin di Gioia365», come in Close. Sotto, soltanto per il Super admin di Gioia365, l'importazione
// (INGR-30, `GhlImportPart`) e la scrittura su GoHighLevel (INGR-19, `GhlWritePart`); al Super admin dell'azienda una riga che lo dice.

function canSee(access: Access): boolean {
  return access.isPlatform || access.can("import_leads");
}

export function GhlReadPage() {
  const t = useT();
  const back = (
    <Link to="/impostazioni/importazione-ed-esportazione" className="g-button" data-variant="ghost">
      {t("importExport.backToSection")}
    </Link>
  );
  return (
    <SettingsLayout section="importExport" title={t("importExport.ghl.title")} actions={back}>
      <GhlReadSection />
    </SettingsLayout>
  );
}

function GhlReadSection() {
  const t = useT();
  const access = useAccess();
  const organization = access.context.organization.name;
  return (
    <div className="g-ci">
      <AboutBox title={t("ghlRead.about.title")}>
        <p>{t("ghlRead.about.text")}</p>
        <h2>{t("ghlRead.about.whoTitle")}</h2>
        <p>{t("ghlRead.about.who", { organization })}</p>
      </AboutBox>
      {canSee(access) ? <KeySection /> : <Reason>{t("ghlRead.denied", { organization })}</Reason>}
    </div>
  );
}

function KeySection() {
  const t = useT();
  const tab = useTab();
  const titleId = useId();
  const status = useQuery(ghlReadQuery(tab));
  return (
    <section className="g-ci__set g-ci__nightly" aria-labelledby={titleId}>
      <div className="g-ci__set-title">
        <h2 id={titleId}>{t("ghlRead.title")}</h2>
        <p className="g-ci__muted">{t("ghlRead.intro")}</p>
      </div>
      {status.data ? (
        status.data.canManage ? (
          <Manage data={status.data} />
        ) : (
          <ReadOnly data={status.data} />
        )
      ) : status.isError ? (
        <div className="g-ci__problem">
          <Reason tone="warning" role="alert">
            {messageForError(t, status.error)}
          </Reason>
          <Button onClick={() => void status.refetch()}>{t("common.retry")}</Button>
        </div>
      ) : (
        <p className="g-ci__muted">{t("common.loading")}</p>
      )}
    </section>
  );
}

/** Una risorsa della prova: «Contatti: 1.234», oppure perché non si è letta. */
function countLine(t: ReturnType<typeof useT>, locale: ReturnType<typeof useLocale>["locale"], what: "contacts" | "conversations", result: GhlProbeCount): string {
  const label = t(`ghlRead.resource.${what}`);
  if (result.outcome === "ok" && result.count !== null) return t("ghlRead.count", { what: label, count: formatCount(locale, result.count) });
  const outcome = result.outcome === "ok" ? "unavailable" : result.outcome;
  return t(`ghlRead.outcome.${outcome}`, { what: label });
}

/** L'ultima prova di lettura: quando e chi, i due numeri e, se GoHighLevel ha rifiutato, gli ambiti che servono. */
function ProbeLines({ data }: { data: GhlReadStatus }) {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const probe = data.lastProbe;
  if (!probe) return <p>{t("ghlRead.probeNone")}</p>;
  const when = whenParts(access.context.settings.timeZone, probe.at);
  const refused = [probe.contacts, probe.conversations].some((result) => result.outcome === "rejected" || result.outcome === "forbidden");
  const ok = [probe.contacts, probe.conversations].every((result) => result.outcome === "ok" && result.count !== null);
  return (
    <>
      <div className="g-ci__ghl-state" data-tone={ok ? "success" : "neutral"}>
        {ok ? <Icon name="check" /> : null}
        <div>
          {ok ? <strong>{t("ghlRead.probeOk")}</strong> : null}
          <ul className="g-ci__ghl-counts" aria-label={t("ghlRead.probeTitle")}>
            <li>{countLine(t, locale, "contacts", probe.contacts)}</li>
            <li>{countLine(t, locale, "conversations", probe.conversations)}</li>
          </ul>
          <p className="g-ci__muted">{probe.by ? t("ghlRead.probeWhen", { ...when, person: probe.by }) : t("ghlRead.probeWhenBySomeone", when)}</p>
        </div>
      </div>
      {refused ? <Reason tone="warning">{t("ghlRead.scopes")}</Reason> : null}
    </>
  );
}

function ReadOnly({ data }: { data: GhlReadStatus }) {
  const t = useT();
  return (
    <div className="g-ci__nightly-part">
      {data.connected ? (
        <div className="g-ci__ghl-state" data-tone="success">
          <Icon name="check" />
          <div>
            <strong>{t("ghlRead.readOnlyOn")}</strong>
          </div>
        </div>
      ) : (
        <p>
          <strong>{t("ghlRead.readOnlyOff")}</strong>
        </p>
      )}
      {data.connected ? <ProbeLines data={data} /> : null}
      <WhoManages />
    </div>
  );
}

/** Chi salva la chiave; al Super admin di Gioia365 che guarda come persona del team la scorciatoia, come in «Ogni sera da Close». */
function WhoManages() {
  const t = useT();
  const access = useAccess();
  return <PlatformShortcut who={t("ghlRead.readOnlyWho", { organization: access.context.organization.name })} shortcut={t("ghlRead.platformShortcut")} />;
}

/** Al Super admin dell'azienda, sotto la chiave: importazione e scrittura restano del Super admin di Gioia365 (INGR-29, INGR-30). */
function PlatformParts() {
  const t = useT();
  return (
    <div className="g-ci__nightly-part">
      <PlatformShortcut who={t("ghlRead.platformPartsWho")} shortcut={t("ghlRead.platformPartsShortcut")} />
    </div>
  );
}

/** Il testo `who`, oppure, al Super admin di Gioia365 entrato come persona del team, `shortcut` col pulsante per entrare come lui. */
function PlatformShortcut({ who, shortcut }: { who: string; shortcut: string }) {
  const t = useT();
  const access = useAccess();
  const { platformAdmin } = useMenuSubject();
  const router = useRouter();
  const [entering, setEntering] = useState(false);
  if (!platformAdmin || access.isPlatform) return <p className="g-ci__muted">{who}</p>;
  const enter = async () => {
    if (entering) return;
    setEntering(true);
    try {
      await switchTab(router, { kind: "platform", organizationId: access.context.organization.id }, router.state.location.href);
    } finally {
      setEntering(false);
    }
  };
  return (
    <>
      <p>{shortcut}</p>
      <div className="g-ci__nightly-actions">
        <Button onClick={() => void enter()} disabled={entering}>
          {t("ghlRead.platformShortcutButton")}
        </Button>
      </div>
    </>
  );
}

function HowTo() {
  const t = useT();
  return (
    <details className="g-ci__ghl-how">
      <summary>{t("ghlRead.howTitle")}</summary>
      <ol>
        <li>{t("ghlRead.howStep1")}</li>
        <li>{t("ghlRead.howStep2")}</li>
        <li>{t("ghlRead.howStep3")}</li>
        <li>{t("ghlRead.howStep4")}</li>
        <li>{t("ghlRead.howStep5")}</li>
      </ol>
      <p>{t("ghlRead.howLocation")}</p>
    </details>
  );
}

function Manage({ data }: { data: GhlReadStatus }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const access = useAccess();
  const timeZone = access.context.settings.timeZone;
  const actions = useGhlReadActions(tab);
  const tokenId = useId();
  const locationId = useId();
  const [token, setToken] = useState("");
  const [location, setLocation] = useState("");
  // Il rifiuto sotto il campo che riguarda: il Location ID che GoHighLevel non trova sotto la posizione, il resto sotto il token.
  const [keyError, setKeyError] = useState<{ field: "token" | "location"; message: string } | undefined>(undefined);
  // La conferma vale per la chiave che c'era quando è stata chiesta: una chiave che cambia la chiude.
  const savedAt = data.key?.savedAt ?? null;
  const [confirmFor, setConfirmFor] = useState<string | null>(null);
  // Con una chiave salvata i campi restano chiusi dietro «Cambia la chiave»: si vede subito che la chiave c'è (Lucio 24/10/2026).
  const [changing, setChanging] = useState(false);
  const showForm = !data.key || changing;
  const confirmRemoval = savedAt !== null && confirmFor === savedAt;
  const confirmButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirmRemoval) confirmButton.current?.focus();
  }, [confirmRemoval]);

  const ready = token.trim() !== "" && location.trim() !== "";
  const saveKey = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || actions.saveKey.isPending) return;
    setKeyError(undefined);
    actions.saveKey.mutate(
      { token: token.trim(), locationId: location.trim() },
      {
        onSuccess: () => {
          setToken("");
          setLocation("");
          setChanging(false);
          toast.show(t("ghlRead.keySavedToast"), "success");
        },
        // Il token resta nel campo per correggerlo; non va mai nel messaggio.
        onError: (error) => setKeyError({ field: error instanceof ApiRequestError && error.code === "ghl_location_not_found" ? "location" : "token", message: messageForError(t, error) }),
      },
    );
  };
  const removeKey = () =>
    actions.removeKey.mutate(undefined, {
      onSuccess: () => {
        setConfirmFor(null);
        toast.show(t("ghlRead.keyRemovedToast"), "success");
      },
      onError: (error) => toast.show(messageForError(t, error), "warning"),
    });
  const probe = () =>
    actions.probe.mutate(undefined, {
      onSuccess: () => toast.show(t("ghlRead.probeToast"), "success"),
      onError: (error) => toast.show(messageForError(t, error), "warning"),
    });

  const saved = data.key ? whenParts(timeZone, data.key.savedAt) : null;
  return (
    <>
      <div className="g-ci__nightly-part">
        <h3>{t("ghlRead.keyTitle")}</h3>
        {data.key && saved ? (
          <div className="g-ci__ghl-state" data-tone="success">
            <Icon name="check" />
            <div>
              <strong>{t("ghlRead.keyActive")}</strong>
              <p>
                {data.key.savedBy
                  ? t("ghlRead.keySaved", { date: saved.date, time: saved.time, person: data.key.savedBy })
                  : t("ghlRead.keySavedBySomeone", { date: saved.date, time: saved.time })}
              </p>
              <p className="g-ci__muted">{t("ghlRead.keyLocation", { location: data.key.locationId })}</p>
            </div>
          </div>
        ) : (
          <p>{t("ghlRead.keyNone")}</p>
        )}
        {data.key && !showForm && !confirmRemoval ? (
          <div className="g-ci__nightly-actions">
            <Button
              onClick={() => {
                // Il Location ID salvato si vede già nel campo, modificabile (Lucio 4/11/2026); il token no, mai.
                setLocation(data.key?.locationId ?? "");
                setChanging(true);
              }}
            >
              {t("ghlRead.keyChange")}
            </Button>
            <Button onClick={() => setConfirmFor(savedAt)}>{t("ghlRead.keyRemove")}</Button>
          </div>
        ) : null}
        {showForm ? (
          <>
            <HowTo />
            <form className="g-ci__nightly-key" onSubmit={saveKey} noValidate>
              <TextField
                id={tokenId}
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                label={t("ghlRead.tokenLabel")}
                hint={t("ghlRead.tokenHint")}
                value={token}
                onChange={(event) => {
                  setToken(event.target.value);
                  setKeyError(undefined);
                }}
                error={keyError?.field === "token" ? keyError.message : undefined}
              />
              <TextField
                id={locationId}
                autoComplete="off"
                spellCheck={false}
                label={t("ghlRead.locationLabel")}
                hint={t("ghlRead.locationHint")}
                value={location}
                onChange={(event) => {
                  setLocation(event.target.value);
                  setKeyError(undefined);
                }}
                error={keyError?.field === "location" ? keyError.message : undefined}
              />
              <div className="g-ci__nightly-actions">
                <Button type="submit" variant="primary" disabled={!ready || actions.saveKey.isPending}>
                  {t(actions.saveKey.isPending ? "ghlRead.keySaving" : "ghlRead.keySave")}
                </Button>
                {data.key ? (
                  <Button
                    onClick={() => {
                      setChanging(false);
                      setToken("");
                      setLocation("");
                      setKeyError(undefined);
                    }}
                  >
                    {t("ghlRead.keyChangeCancel")}
                  </Button>
                ) : null}
              </div>
            </form>
          </>
        ) : null}
        {data.key && confirmRemoval ? (
          <div className="g-ci__nightly-confirm" role="group" aria-label={t("ghlRead.keyRemove")}>
            <p>{t("ghlRead.keyRemoveConfirm")}</p>
            <div className="g-ci__nightly-actions">
              <Button ref={confirmButton} variant="primary" onClick={removeKey} disabled={actions.removeKey.isPending}>
                {t("ghlRead.keyRemoveYes")}
              </Button>
              <Button onClick={() => setConfirmFor(null)}>{t("common.cancel")}</Button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="g-ci__nightly-part">
        <h3>{t("ghlRead.probeTitle")}</h3>
        <p className="g-ci__muted">{t("ghlRead.probeText")}</p>
        {data.connected ? <ProbeLines data={data} /> : <p>{t("ghlRead.probeNeedsKey")}</p>}
        <div className="g-ci__nightly-actions">
          <Button onClick={probe} disabled={!data.connected || actions.probe.isPending}>
            {t(actions.probe.isPending ? "ghlRead.probing" : "ghlRead.probeStart")}
          </Button>
        </div>
      </div>

      {data.importer ? <GhlImportPart importer={data.importer} connected={data.connected} /> : null}
      {access.isPlatform ? <GhlWritePart connected={data.connected} /> : <PlatformParts />}
    </>
  );
}
